import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Calculator, Clock } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './DateAddSubtractCalculatorPage/components/CalculationForm';
import ResultsOverview from './DateAddSubtractCalculatorPage/components/ResultsOverview';
import CalendarView from './DateAddSubtractCalculatorPage/components/CalendarView';
import QuickAdjustmentSidebar from './DateAddSubtractCalculatorPage/components/QuickAdjustmentSidebar';
import SavedCalculationsSidebar from './DateAddSubtractCalculatorPage/components/SavedCalculationsSidebar';
import TipsSidebar from './DateAddSubtractCalculatorPage/components/TipsSidebar';
import InfoContent from './DateAddSubtractCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  startDate: new Date().toISOString().split('T')[0],
  mode: 'add', // 'add' или 'subtract'
  unit: 'days', // 'days', 'weeks', 'months', 'years'
  value: '30',
  includeWorkingDays: false,
  includeHolidays: false,
  region: 'default', // 'default', 'moscow', 'spb', и т.д.
  customHolidays: [],
  returnFormat: 'full', // 'short', 'medium', 'full'
  showWeekday: true
};

// Определение регионов России
export const russianRegions = [
  { id: 'default', name: 'Общероссийские праздники' },
  { id: 'moscow', name: 'Москва' },
  { id: 'spb', name: 'Санкт-Петербург' },
  { id: 'tatarstan', name: 'Республика Татарстан' },
  { id: 'bashkortostan', name: 'Республика Башкортостан' },
  { id: 'crimea', name: 'Республика Крым' },
  { id: 'krasnodar', name: 'Краснодарский край' },
  { id: 'novosibirsk', name: 'Новосибирская область' },
  { id: 'sverdlovsk', name: 'Свердловская область' }
];

// Определение единиц измерения
export const timeUnits = [
  { id: 'days', name: 'Дни', namePlural: 'дни', nameGenitive: 'дней' },
  { id: 'weeks', name: 'Недели', namePlural: 'недели', nameGenitive: 'недель' },
  { id: 'months', name: 'Месяцы', namePlural: 'месяцы', nameGenitive: 'месяцев' },
  { id: 'years', name: 'Годы', namePlural: 'годы', nameGenitive: 'лет' },
  { id: 'workingDays', name: 'Рабочие дни', namePlural: 'рабочие дни', nameGenitive: 'рабочих дней' }
];

/**
 * DateAddSubtractCalculatorPage Component - Полный калькулятор добавления и вычитания дат
 * с поддержкой различных форматов, рабочих дней и праздников по регионам РФ.
 */
const DateAddSubtractCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('date-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор добавления и вычитания дат | Gensio';
  const metaDescription = tool?.metaDescription || 'Онлайн калькулятор дат: добавляйте или вычитайте дни, недели, месяцы и годы с учетом выходных, праздников и региональных особенностей. Планируйте сроки, рассчитывайте рабочие дни и важные даты для России.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcsData = localStorage.getItem('savedDateCalculations');
    if (savedCalcsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcsData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'add':
          newState.mode = 'add';
          break;
        case 'subtract':
          newState.mode = 'subtract';
          break;
        case 'working-days':
          newState.includeWorkingDays = true;
          newState.unit = 'workingDays';
          break;
        case 'date-difference':
          // Не обрабатываем этот случай так как это другой калькулятор
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalc = {
      id: Date.now().toString(),
      name: name || `Расчет от ${new Date().toLocaleDateString('ru-RU')}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedCalcs = [...savedCalculations, newSavedCalc];
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedDateCalculations', JSON.stringify(updatedSavedCalcs));

    return newSavedCalc;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalcs = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedDateCalculations', JSON.stringify(updatedSavedCalcs));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Calendar className="w-8 h-8 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор добавления и вычитания дат'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              russianRegions={russianRegions}
              timeUnits={timeUnits}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} timeUnits={timeUnits} />
                <CalendarView results={results} formData={formData} />
              </>
            )}

            {/* --- Информационный контент о календарях и датах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              timeUnits={timeUnits}
            />

            {/* --- Панель сохраненных расчетов --- */}
            <SavedCalculationsSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-cyan-600 focus:ring-cyan-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-cyan-600 focus:ring-cyan-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Date Picker Styling */
        .date-picker {
          @apply p-2 rounded-md;
        }
        
        /* Calendar View Styles */
        .calendar-day {
          @apply w-8 h-8 flex items-center justify-center rounded-full;
        }
        .calendar-day-highlight {
          @apply bg-cyan-500 text-white;
        }
        .calendar-day-start {
          @apply bg-cyan-600 text-white;
        }
        .calendar-day-end {
          @apply bg-cyan-700 text-white;
        }
        .calendar-day-weekend {
          @apply text-red-500 dark:text-red-400;
        }
        .calendar-day-holiday {
          @apply bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800;
        }
      `}</style>
    </div>
  );
};

export default DateAddSubtractCalculatorPage;